// Registre du jour (décisions et signatures attendues) et semaine des échéances, composés à
// partir des listes de dossiers et de courriers (L0-C) et des délégations (L0-A).
import { LIBELLES_ETATS_DOSSIER, type CourrierResume, type DossierResume, type ListePaginee } from '@cc/contrats/ui-metier';
import { Registre, Semaine, Squelette, type ElementRegistre, type JourSemaine } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { useDelegations } from '../../api/requetes';
import { AffichageErreur } from '../../commun/EtatRequete';
import { isoJour } from '../../commun/format';
import { useSession } from '../../session/FournisseurSession';
import { voitDomaine } from '../../session/permissions';
import { LIBELLES_TYPES } from '../administration/FormulaireDelegation';
import { semaineCourante } from './periode';

function useDossiers(parametres: Record<string, string>, actif: boolean) {
  return useQuery({
    queryKey: ['dossiers', 'liste', parametres],
    queryFn: ({ signal }) => requete<ListePaginee<DossierResume>>('/dossiers', { signal, parametres }),
    enabled: actif,
    retry: false,
  });
}

function useCourriers(parametres: Record<string, string>, actif: boolean) {
  return useQuery({
    queryKey: ['courriers', 'liste', parametres],
    queryFn: ({ signal }) => requete<ListePaginee<CourrierResume>>('/courriers', { signal, parametres }),
    enabled: actif,
    retry: false,
  });
}

export function RegistreDuJour() {
  const { contexte } = useSession();
  const voitDossiers = voitDomaine(contexte, 'dossier');
  const voitCourriers = voitDomaine(contexte, 'courrier');
  const decisions = useDossiers({ etat: 'en_attente_decision' }, voitDossiers);
  const retards = useDossiers({ en_retard: 'oui' }, voitDossiers);
  const aSigner = useCourriers({ etat: 'valide', sens: 'depart' }, voitCourriers);

  const enCours = decisions.isLoading || retards.isLoading || aSigner.isLoading;
  const erreur = decisions.error ?? aSigner.error ?? retards.error;
  const elements: ElementRegistre[] = [
    ...(aSigner.data?.elements ?? []).map((c) => ({
      id: `c-${c.id}`,
      reference: c.numero ?? 'sans numéro',
      objet: c.objet,
      sousTitre: c.correspondant_nom,
      etat: { ton: 'signer' as const, libelle: 'À signer' },
      href: `/courriers/${c.id}`,
    })),
    ...(decisions.data?.elements ?? []).map((d) => ({
      id: `d-${d.id}`,
      reference: d.numero ?? 'sans numéro',
      objet: d.demarche.libelle,
      sousTitre: d.service?.nom ?? undefined,
      etat: { ton: 'attente' as const, libelle: 'Décision attendue' },
      href: `/dossiers/${d.id}`,
    })),
    ...(retards.data?.elements ?? []).map((d) => ({
      id: `r-${d.id}`,
      reference: d.numero ?? 'sans numéro',
      objet: d.demarche.libelle,
      sousTitre: `${LIBELLES_ETATS_DOSSIER[d.etat] ?? d.etat}, échéance interne dépassée`,
      etat: { ton: 'retard' as const, libelle: 'En retard' },
      href: `/dossiers/${d.id}`,
    })),
  ];
  if (!voitDossiers && !voitCourriers) return null;
  return (
    <Registre
      titre="Registre du jour"
      niveau={2}
      compteur={enCours || erreur ? undefined : `${elements.length} entrée${elements.length > 1 ? 's' : ''}`}
      elements={elements}
      vide={
        <div style={{ padding: 16 }}>
          {enCours ? (
            <Squelette lignes={3} />
          ) : erreur ? (
            <AffichageErreur erreur={erreur} lot="L0-C" fonction="Le registre des courriers et des dossiers" />
          ) : (
            <p>Aucune signature, décision ou retard en attente.</p>
          )}
        </div>
      }
    />
  );
}

export function SemaineEcheances() {
  const { contexte } = useSession();
  const jours = semaineCourante();
  const delegations = useDelegations();
  const dossiers = useDossiers({ echeance_avant: jours[4]?.date ?? '', limite: '200' }, voitDomaine(contexte, 'dossier'));
  const semaine: JourSemaine[] = jours.map((j) => ({
    date: j.date,
    libelle: j.libelle.charAt(0).toUpperCase() + j.libelle.slice(1),
    evenements: [
      ...(delegations.data ?? [])
        .filter((d) => d.statut === 'en_vigueur' && isoJour(new Date(d.fin)) === j.date)
        .map((d) => ({ id: `del-${d.id}`, titre: `Fin de ${LIBELLES_TYPES[d.type]?.toLowerCase() ?? 'délégation'}`, type: 'echeance' as const, libelleType: 'Échéance' })),
      ...(dossiers.data?.elements ?? [])
        .filter((d) => d.echeance_interne && isoJour(new Date(d.echeance_interne)) === j.date)
        .map((d) => ({ id: `dos-${d.id}`, titre: `Échéance ${d.numero ?? 'dossier'}`, type: (d.en_retard ? 'retard' : 'echeance') as 'retard' | 'echeance', libelleType: 'Échéance de dossier' })),
    ],
  }));
  return (
    <section aria-labelledby="titre-semaine" style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <h2 id="titre-semaine">Semaine</h2>
      <Semaine jours={semaine} aujourdhui={isoJour(new Date())} libelle="Échéances de la semaine" vide="Aucune échéance" />
      {dossiers.error ? (
        <p style={{ fontSize: 12, color: 'var(--cc-encre-2)' }}>Les échéances des dossiers s&apos;afficheront quand le service des dossiers (lot L0-C) sera disponible.</p>
      ) : null}
    </section>
  );
}
